import React from 'react';
import { Flex, Text, View, IconButton, Icon, Icons } from '@smarterservices/smarter-ui';
import { BlockComponentProps, StepBlock as StepBlockType } from '../../types';
import BlockRenderer from '../../BlockRenderer';

/**
 * Props for the StepBlock component
 */
interface StepBlockProps extends BlockComponentProps<StepBlockType> {
  stepNumber?: number;
  totalSteps?: number;
}


/**
 * StepBlock component renders a step with an optional step number
 * 
 * @param {StepBlockProps} props - Component props
 * @returns {React.ReactElement} Rendered step
 */
export const StepBlock = ({ block, stepNumber, totalSteps }: StepBlockProps) => {
  const isLastStep = stepNumber === totalSteps;
  /**
   * Determines the margin for the vertical line based on item position in the timeline
   * @param {number} index - Index of the current item in the items array
   * @returns {string|undefined} CSS margin value
   */
  const figureMargin = (index) => {
    if (index === 0) {
      return '15px 0 0 0';
    } else if (index === totalSteps) {
      return '0 0 5px 0';
    }
  };
  /**
   * Calculates the appropriate height for the vertical line based on item position and whether it has detail content
   * @param {number} index - Index of the current item in the items array
   * @param {boolean} hasDetail - Whether the current item has detail content
   * @returns {string} CSS height value
   */
  const figureHeight = (index, hasDetail) => {
    if (index !== totalSteps) {
      return '100%';
    } else if (index === totalSteps && hasDetail) {
      return '100%';
    } else {
      return '10px';
    }
  };


  return (
    <View as="div" key={`step-number-${stepNumber}`}>
              <Flex alignItems="stretch" style={{ height: '100%' }}>
                
                <Flex.Item className="blockkit-step-container" as="div" position="relative" style={{ height: '100%' }}>
                  {/* Vertical line spanning the entire container */}
                  <div
                    className="blockkit-step-connector"
                    style={{
                      position: 'absolute',
                      top: '2px',
                      bottom: '2px',
                      left: '50%', // center the line within the left padding
                      width: '1px',
                      height: figureHeight(
                        stepNumber,
                        block?.detail && block.detail.length ? true : false
                      ),
                      backgroundColor: '#ccc',
                      margin: figureMargin(stepNumber),
                    }}
                  />
                  <View as="div" className="blockkit-step" margin={isLastStep ? "none none none none" : "none none large none"}>
                    <IconButton
                      as='span'                     
                      shape='circle'                      
                      margin="xxx-small"
                      cursor='default'
                      interaction='readonly'
                      withBorder={true}
                      color={block?.icon?.color || 'secondary'}
                      size={block?.icon?.size || 'small'}
                      className="blockkit-step-icon"
                      screenReaderLabel={
                        block?.icon?.screenReaderLabel
                          ? block?.icon?.screenReaderLabel
                          : `Step ${stepNumber}`
                      }
                    >

                      {(block?.icon?.name && Icons[block?.icon?.name]) ? 
                      <Icon name={block?.icon?.name} color={block?.icon?.color || 'secondary'} size={block?.icon?.size || 'x-small'} /> : 
                      <Text size="small">{stepNumber}</Text>}
                      
                    </IconButton>
                  </View>
                </Flex.Item>
                <Flex.Item
                  as="div"
                  shouldGrow
                  shouldShrink
                  align="start"
                  style={{ height: '100%' }}
                >
                  <View as="div" textAlign="start" margin="none none small none">
                    
                    {block?.title &&
                      <View as="div" margin="xx-small none none x-small">
                        {block?.title}
                      </View>     
                    }

                    {block?.elements && (
                      <View as="div" margin="xx-small none none x-small">
                        <BlockRenderer blocks={block?.elements} />
                      </View>
                    )}
                  </View>
                </Flex.Item>
              </Flex>
            </View>



  );
};

export default StepBlock;
